<template>
  <radio-group-root v-model="selectedOtpMethod" :aria-label="$t.mfa.otpMethod" class="f-col gap-4">
    <define-verification-option v-slot="{ icon, label, value, $slots }">
      <radio-group-item
        :id="value"
        class="grid cols-[min-content_1fr] items-center gap-x-2 b b-transparent p-4 outline-none"
        :class="[
          brandClasses.base,
          selectedOtpMethod === value
            ? brandClasses.selected
            : brandClasses.unselected,
        ]"
        :style="brandStyles?.item"
        :value
      >
        <vf-icon :name="icon" size="lg" :style="brandStyles?.icon" />
        <label class="fw-bold" :for="value">{{ label }}</label>
        <div class="col-start-2 flex items-center" :class="brandClasses.details">
          <component :is="$slots.default" />
        </div>
      </radio-group-item>
    </define-verification-option>

    <verification-option icon="mail" :label="$t.mfa.email" value="email">
      {{ lookupData.email }}
    </verification-option>

    <verification-option v-if="lookupData.sms" icon="chat" :label="$t.mfa.textMessage" value="sms">
      <vf-icon name="horizontal-ellipsis" size="lg" />
      <vf-icon name="horizontal-ellipsis" size="lg" />
      {{ lookupData.sms }}
    </verification-option>
  </radio-group-root>
  <div class="f-col grow pt-6 space-y-6">
    <vf-button class="w-full" :loading @click="handleSendCode">
      {{ $t.mfa.sendCode }}
    </vf-button>

    <div v-if="lookupData.authMethods.includes('password')" class="w-full text-center" :class="enrollmentBrandClasses.tryAnotherWay">
      <vf-link class="fw-bold" @click="emit('tryAnotherWay')">
        {{ $t.mfa.tryAnotherWay }}
      </vf-link>
    </div>
    <p v-if="lookupData.sms" class="text-center text-sm c-grey-10">
      <base-interpolator :content="$t.mfa.smsTerms">
        <template #terms="{ args: [message] }">
          <vf-link target="_blank" to="/sms-terms">
            {{ message }}
          </vf-link>
        </template>
      </base-interpolator>
    </p>
  </div>
</template>

<script lang="ts" setup>
import type { LookupData } from '#root/api/clients/consumer/data-contracts'
import type { IconName } from '#types/icon'
import { RadioGroupItem, RadioGroupRoot } from 'reka-ui'

const { lookupData } = defineProps<{ lookupData: LookupData }>()

const emit = defineEmits<{
  tryAnotherWay: []
  continue: [method: 'email' | 'sms']
}>()

const [DefineVerificationOption, VerificationOption] = createReusableTemplate<{
  icon: IconName
  label: string
  value: string
}>()

const appConfig = useAppConfig()
const { brandClasses: enrollmentBrandClasses } = appConfig.components.dialog.simplifiedEnrollment
const { brandClasses, brandStyles } = appConfig.components.form.simplifiedEnrollment.verificationOptions
const { authentication } = useApi()
const selectedOtpMethod = ref<'email' | 'sms'>('email')
const loading = ref(false)
const recaptcha = useRecaptcha()

const handleSendCode = async () => {
  loading.value = true
  try {
    const captchaResponse = await recaptcha.execute('login')
    const { codeChallenge } = await getPkceCodePair()

    await authentication.$mfa({
      authMethod: selectedOtpMethod.value,
      username: lookupData.email,
      codeChallenge,
      recaptcha_response: captchaResponse,
    })

    emit('continue', selectedOtpMethod.value)
  }
  catch (err) {
    console.error('Error sending MFA code:', err)
  }
  finally {
    loading.value = false
  }
}
</script>
